import classnames from "classnames";
import * as _ from "lodash";
import * as React from "react";
import * as S from "@styled";

import { connect } from "react-redux";
import { updateParameter } from "../../actions/UpdateParameter";
import { State } from "../../model/State";
import LabelWithTooltip from "shared/components/LabelWithTooltip";

import { DispatchType } from "../../../shared/utilities";
import EnvironmentVariableExpander from "../EnvironmentVariableExpander";
import { ParameterEditorValue } from "../../model/ParameterEditorValue";
import { ParameterEditorMode } from "../ParameterInput";
import MonacoEditor from "../../../shared/monaco/components/MonacoEditor";
import CustomToggle from "shared/components/CustomToggle";
import { ValidationError } from "processEditor/model/ValidationData";

export const TriggerInput: React.FC<TriggerInputProps> = (props) => {
    const [isUnmasked, setIsUnmasked] = React.useState<boolean>(false);

    const handleUnmasking = () => {
        setIsUnmasked((isUnmasked) => !isUnmasked);
    };

    const value = _.get(props.existingParameters, props.name);
    // ACC-7467 Set default value if key is not found in state but a default value is passed.
    if (value === undefined && props.defaultValue) {
        props.onChange(props.defaultValue);
    }

    const handleEditorChange = (newValue: string | undefined) => {
        if (_.isEqual(newValue, value)) {
            // no actual change
            return;
        }
        props.onChange(newValue);
        if (props.onUpdated) {
            props.onUpdated(newValue);
        }
    };

    const valueOrDefault = value != undefined ? value : props.defaultValue;
    const renderSingleLineMonacoEditor = () => (
        <S.ExpressionEditorContainer
            variant={!_.isEmpty(props.validationErrors) ? S.ExpressionEditorContainerState.error : S.ExpressionEditorContainerState.primary}
            style={{ ...(props.isSecret ? { display: 'flex' } : {}) }}
        >
            <MonacoEditor
                value={valueOrDefault}
                mode={ParameterEditorMode.plaintext}
                onChange={handleEditorChange}
                readOnly={props.disabled || props.disableEdit}
            />

            {props.isSecret && (
                <button onClick={handleUnmasking}>
                    <i className="ci-show" />
                </button>
            )}
        </S.ExpressionEditorContainer>
    );

    function getInputControl() {
        if (props.inputMode === InputMode.boolean) {
            let booleanValue: boolean = value == null ? false : value;
            if (props.disabled && props.defaultValue != null) {
                booleanValue = !!props.defaultValue;
            }
            return (
                <CustomToggle
                    key={props.name}
                    checked={booleanValue}
                    onChange={handleSwitchChange}
                    disabled={props.disabled || props.disableEdit}
                />
            );
        } else if (props.inputMode === InputMode.text && !props.disabled && !props.isSecret) {
            return renderSingleLineMonacoEditor();
        } else if (props.isSecret && !isUnmasked) {
            return <S.PasswordInput
                isFullWidth={true}
                name={props.name}
                key={props.name}
                autoComplete="new-password"
                disabled={props.disabled || props.disableEdit}
                onChange={onInputChange}
                defaultValue={value || props.defaultValue}
                onUnmask={handleUnmasking}
            />;
        } else if (props.isSecret && isUnmasked) {
            return renderSingleLineMonacoEditor();
        } else {
            return <S.Input
                isFullWidth={true}
                list="environmentVariables"
                key={props.name}
                type={InputMode[props.inputMode]}
                min={props.minValue}
                disabled={props.disabled || props.disableEdit}
                onChange={onInputChange}
                defaultValue={value || props.defaultValue}
            />;
        }

    }

    function onInputChange(event: any) {
        const newValue = isNaN(event.target.valueAsNumber) ? event.target.value : event.target.valueAsNumber;
        props.onChange(newValue);
        if (props.onUpdated) {
            props.onUpdated(newValue);
        }
    }
    const handleSwitchChange = (checked: boolean, event: any, id: string) => {
        props.onChange(checked);
        if (props.onUpdated) {
            props.onUpdated(checked);
        }
    };

    let label = null;
    if (props.label) {
        let labelContent;
        const shouldNotDisplayIcon = props.showTooltipIcon != undefined ? !props.showTooltipIcon : false;
        if (props.description) {
            labelContent = <LabelWithTooltip
                label={props.label}
                defaultValue={props.defaultValue ? props.defaultValue : null}
                description={props.description}
                shouldNotDisplayIcon={shouldNotDisplayIcon}
                doNotHumanizeLabel={true}
                useHover
            />;
        } else {
            labelContent = (
                <>
                    {props.label}
                </>
            );
        }


        label = (
            <S.FormGroup $inline={true} $noMargin={true}>
                <S.Label>
                    <S.FlexContainer centerItems={true}>
                        {labelContent}
                        <EnvironmentVariableExpander parameterInputValue={value || props.defaultValue} shouldDisplayIcon={true} />
                    </S.FlexContainer>
                </S.Label>
                {props.inputMode === InputMode.boolean ? getInputControl() : null}
            </S.FormGroup>
        );
    }
    
    return (
        <S.FormGroup 
            className={classnames(props.extraClasses as string, "trigger-input-selector")} 
            variant={!_.isEmpty(props.validationErrors) ? S.FormGroupVariant.error : S.FormGroupVariant.primary}
        >
            {label}
            {props.inputMode !== InputMode.boolean ? getInputControl() : null}
        </S.FormGroup>
    );
};

interface TriggerInputOwnProps {
    elementId: string | undefined;
    name: string;
    inputMode: InputMode;
    existingParameters: any;
    disabled?: boolean;
    label?: string;
    isSecret?: boolean;
    isUnmasked?: boolean;
    description?: string;
    defaultValue?: string | number | boolean | ParameterEditorValue;
    minValue?: number;
    extraClasses?: string;
    showTooltipIcon?: boolean;
    onUpdated?: (newValue: any) => void;
}

interface TriggerInputStateProps {
    disableEdit: boolean | undefined;
    validationErrors: ValidationError[] | undefined;
}

interface TriggerInputDispatchProps {
    onChange: (newValue: any) => void;
}

type TriggerInputProps = TriggerInputOwnProps & TriggerInputDispatchProps & TriggerInputStateProps;

const mapStateToProps = (state: State, ownProps: TriggerInputOwnProps): TriggerInputStateProps => {
    let validationElements: ValidationError[] | undefined = ownProps.elementId ? state.validation.validationDictionary[ownProps.elementId] : undefined;
    if (ownProps.name && validationElements) {
        // FRP-1481: Get parameter specific validation errors, this might be the cause if a parameter doesnt show that it has errors.
        // To fix it you should give the ValidationError the parameterKey property which should match with ownProps.name
        validationElements = validationElements.filter((error: ValidationError) => {
            const isExactMatch = error.parameterKey?.toLowerCase() === ownProps.name.toLowerCase();
            const isNestedParameter = ownProps.name.startsWith('parameters.') && error.parameterKey === 'parameters';
            return isExactMatch || isNestedParameter;
        });
    }

    return {
        disableEdit: state.uiState.disableEdit,
        validationErrors: validationElements
    };
};

const mapDispatchToProps = (dispatch: DispatchType<State>, ownProps: TriggerInputOwnProps): TriggerInputDispatchProps => {
    return {
        onChange: (newValue: any) => dispatch(updateParameter(ownProps.elementId, ownProps.name, newValue))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TriggerInput);

export enum InputMode {
    text,
    boolean,
    number,
    password
}
